import type {
  ProcedureTemplateDetail,
  SchedulingResource,
  StepScheduleTemplate,
  Procedure,
} from '@/types/cgt'
import { addDays, todayISO } from '@/lib/scheduling'

// ─── Mock scheduling data ──────────────────────────────────────────────────────
//
// Mirrors the scheduling diagram (image 1): a transplant procedure template is a
// chain of four "fields", each +1 day after the previous, each booking equipment
// + a shared staff resource ("Resource A") for a number of hours. Anchored to the
// week of Jul 8–15, 2024 to match the sample calendar (image 2). Resource A is
// deliberately given a 5 hr/day cap so the two WED fields (3 + 3 hrs) collide —
// giving the UI a built-in conflict to render.

const PROCEDURE_TEMPLATE_ID = 'proc-transplant-001'

// Stable step IDs — the four "fields" of the chain.
const STEP = {
  field1: 'step-field-1',
  field2: 'step-field-2',
  field3: 'step-field-3',
  field4: 'step-field-4',
} as const

// ─── Resources (equipment + one shared staff member) ──────────────────────────

export const mockProcedureResources: SchedulingResource[] = [
  { id: 'equip-1', name: 'Equipment 1', kind: 'equipment', capacityHoursPerDay: 8 },
  { id: 'equip-2', name: 'Equipment 2', kind: 'equipment', capacityHoursPerDay: 8 },
  { id: 'equip-3', name: 'Equipment 3', kind: 'equipment', capacityHoursPerDay: 8 },
  { id: 'equip-4', name: 'Equipment 4', kind: 'equipment', capacityHoursPerDay: 8 },
  { id: 'equip-5', name: 'Equipment 5', kind: 'equipment', capacityHoursPerDay: 8 },
  { id: 'equip-7', name: 'Equipment 7', kind: 'equipment', capacityHoursPerDay: 8 },
  { id: 'equip-8', name: 'Equipment 8', kind: 'equipment', capacityHoursPerDay: 8 },
  { id: 'resource-a', name: 'Resource A', kind: 'staff', capacityHoursPerDay: 5 },
]

// ─── Procedure template (the field chain + day-lag edges) ─────────────────────

export const mockProcedureTemplate: ProcedureTemplateDetail = {
  id: PROCEDURE_TEMPLATE_ID,
  name: 'Standard Autologous Transplant',
  description: 'Four-field collection → processing → release → distribution chain.',
  createdBy: 'admin',
  createdAt: '2024-06-01T00:00:00.000Z',
  updatedAt: '2024-06-01T00:00:00.000Z',
  steps: [
    {
      stepId: STEP.field1,
      sortOrder: 0,
      formDefinitionId: 'def-collection',
      formName: 'Field 1 · Collection',
      formType: 'collection',
      predecessorStepIds: [],
      predecessors: [],
    },
    {
      stepId: STEP.field2,
      sortOrder: 1,
      formDefinitionId: 'def-processing',
      formName: 'Field 2 · Processing',
      formType: 'processing',
      predecessorStepIds: [STEP.field1],
      predecessors: [{ stepId: STEP.field1, sortOrder: 0, formName: 'Field 1 · Collection' }],
    },
    {
      stepId: STEP.field3,
      sortOrder: 2,
      formDefinitionId: 'def-release',
      formName: 'Field 3 · Product Release Review',
      formType: 'product_release_review',
      predecessorStepIds: [STEP.field2],
      predecessors: [{ stepId: STEP.field2, sortOrder: 1, formName: 'Field 2 · Processing' }],
    },
    {
      stepId: STEP.field4,
      sortOrder: 3,
      formDefinitionId: 'def-distribution',
      formName: 'Field 4 · Distribution',
      formType: 'distribution',
      predecessorStepIds: [STEP.field2],
      predecessors: [{ stepId: STEP.field2, sortOrder: 1, formName: 'Field 2 · Processing' }],
    },
  ],
  edges: [
    {
      predecessorStepId: STEP.field1,
      successorStepId: STEP.field2,
      edgeType: 'finish',
      minLagDays: 1,
    },
    {
      predecessorStepId: STEP.field2,
      successorStepId: STEP.field3,
      edgeType: 'finish',
      minLagDays: 1,
    },
    {
      predecessorStepId: STEP.field2,
      successorStepId: STEP.field4,
      edgeType: 'finish',
      minLagDays: 1,
    },
  ],
}

// ─── Per-step resource templates (hours from image 1) ──────────────────────────

export const mockProcedureStepTemplates: StepScheduleTemplate[] = [
  {
    stepId: STEP.field1,
    durationHours: 5,
    allocations: [
      { resourceId: 'equip-1', hours: 5 },
      { resourceId: 'equip-2', hours: 3 },
      { resourceId: 'resource-a', hours: 3 },
    ],
  },
  {
    stepId: STEP.field2,
    durationHours: 5,
    allocations: [
      { resourceId: 'equip-3', hours: 5 },
      { resourceId: 'equip-4', hours: 3 },
      { resourceId: 'resource-a', hours: 3 },
    ],
  },
  {
    stepId: STEP.field3,
    durationHours: 5,
    allocations: [
      { resourceId: 'equip-1', hours: 5 },
      { resourceId: 'equip-5', hours: 3 },
      { resourceId: 'resource-a', hours: 3 },
    ],
  },
  {
    stepId: STEP.field4,
    durationHours: 5,
    allocations: [
      { resourceId: 'equip-7', hours: 5 },
      { resourceId: 'equip-8', hours: 3 },
      { resourceId: 'resource-a', hours: 3 },
    ],
  },
]

// ─── Upcoming procedures + their anchor dates ──────────────────────────────────

interface MockProcedure extends Procedure {
  anchorDate: string
}

// Anchor the sample procedures around the current date so the calendar always
// opens on relevant dates: one starting today, one next week, one next month.
const TODAY = todayISO()
const PROCEDURE_OFFSETS = [0, 7, 30] as const

function procedureStartedAt(offset: number): string {
  return `${addDays(TODAY, offset)}T00:00:00.000Z`
}

export const mockProcedures: MockProcedure[] = [
  {
    id: 'TSP-2024-001',
    patientMrn: 'MRN-1001',
    procedureTemplateId: PROCEDURE_TEMPLATE_ID,
    status: 'in_progress',
    startedAt: procedureStartedAt(PROCEDURE_OFFSETS[0]),
    scheduledDate: null,
    dueDate: null,
    workOrderNumber: null,
    mfgNumber: null,
    quantity: 1,
    createdBy: 'admin',
    createdAt: procedureStartedAt(-7),
    updatedAt: procedureStartedAt(-7),
    procedureTemplate: { id: PROCEDURE_TEMPLATE_ID, name: mockProcedureTemplate.name },
    anchorDate: addDays(TODAY, PROCEDURE_OFFSETS[0]),
  },
  {
    id: 'TSP-2024-002',
    patientMrn: 'MRN-1002',
    procedureTemplateId: PROCEDURE_TEMPLATE_ID,
    status: 'in_progress',
    startedAt: procedureStartedAt(PROCEDURE_OFFSETS[1]),
    scheduledDate: null,
    dueDate: null,
    workOrderNumber: null,
    mfgNumber: null,
    quantity: 1,
    createdBy: 'admin',
    createdAt: procedureStartedAt(-7),
    updatedAt: procedureStartedAt(-7),
    procedureTemplate: { id: PROCEDURE_TEMPLATE_ID, name: mockProcedureTemplate.name },
    anchorDate: addDays(TODAY, PROCEDURE_OFFSETS[1]),
  },
  {
    id: 'TSP-2024-003',
    patientMrn: 'MRN-1003',
    procedureTemplateId: PROCEDURE_TEMPLATE_ID,
    status: 'in_progress',
    startedAt: procedureStartedAt(PROCEDURE_OFFSETS[2]),
    scheduledDate: null,
    dueDate: null,
    workOrderNumber: null,
    mfgNumber: null,
    quantity: 1,
    createdBy: 'admin',
    createdAt: procedureStartedAt(-7),
    updatedAt: procedureStartedAt(-7),
    procedureTemplate: { id: PROCEDURE_TEMPLATE_ID, name: mockProcedureTemplate.name },
    anchorDate: addDays(TODAY, PROCEDURE_OFFSETS[2]),
  },
]

export const mockProcedureAnchors: Record<string, string> = Object.fromEntries(
  mockProcedures.map((t) => [t.id, t.anchorDate]),
)
